import { useState } from "react";
import { Button } from "@/components/ui/PageHeader";
import { prepare, submit, type ContributionFeature, type DispatchOutcome, type PreparedDecision } from "@/api/composer";

/**
 * Prepare a compose handoff where the member chose to contribute, then confirm it.
 * Confirm posts for real: it creates a Voice or Question, or returns a route.
 */
export function ContributionReviewPanel({ text, feature }: { text: string; feature: ContributionFeature }) {
  const [prepared, setPrepared] = useState<{ text: string; data: PreparedDecision } | null>(null);
  const [outcome, setOutcome] = useState<DispatchOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = prepared?.text === text ? prepared.data : null;

  async function run(step: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try { await step(); } catch (e) { setError(e instanceof Error ? e.message : "Request failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="font-semibold">Review in {feature}</p>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="secondary" disabled={busy} isLoading={busy && !current}
          onClick={() => run(async () => { setOutcome(null); setPrepared({ text, data: (await prepare(text, feature)).data }); })}>
          Prepare
        </Button>
        {current && !outcome && (
          <Button size="sm" disabled={busy} onClick={() => run(async () => {
            setOutcome((await submit({ text, preparationId: current.submissionId })).data);
          })}>Confirm (posts for real)</Button>
        )}
      </div>
      {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
      {current && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt>Prepared action</dt><dd>{current.action}</dd>
          <dt>Choice applied</dt><dd>{current.contribution?.applied ? "Yes" : "No"}</dd>
          {current.contribution?.message && <><dt>Why not</dt><dd>{current.contribution.message}</dd></>}
          <dt>Safe</dt><dd>{current.isActionSafe ? "Yes" : "No"}</dd>
          <dt>Audience</dt><dd>{current.audienceDescription}</dd>
          <dt>Expires</dt><dd>{current.expiresAt}</dd>
        </dl>
      )}
      {outcome && (
        <p className="mt-2">
          {outcome.kind}{outcome.post ? `: ${outcome.post.type} ${outcome.post.id}` : ""}
          {outcome.route ? `: ${outcome.route.target}` : ""}{outcome.decline ? `: ${outcome.decline.message}` : ""}
        </p>
      )}
    </div>
  );
}
